import { createMuiTheme } from '@material-ui/core';

const colors = {
  orange: '#F26923',
  teal: '#3C9BA0',
  green: '#0F4147',
  burntYellow: '#F4A52E',
  darkBlue: '#323B45',
  coolBlue: '#F2F6F6',
  black: '#231F20',
  darkGrey: '#353434',
  mediumGrey: '#6C6D6D',
  lightGrey: '#D8D8D8',
  coolGrey: '#F2F3F0',
  white: '#FFF',
  // Secondary color pallette
  darkPurple: '#4F0F44',
  darkBrown: '#2F1413',
  lightGreen: '#9DD485',
  magenta: '#892C23',
  red: '#D0021C',
  sky: '#9AD9E7',
  peas: '#B8B03C',
  peach: '#FEDFCE',
  khaki: '#F9D296',
  yellow: '#FFFF99',
};

// Font sizes and other styles common to both the light and dark themes go here
const globalThemeStyles = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 321,
      md: 600,
      lg: 1024,
      xl: 1920,
    },
  },
  palette: {
    text: {
      primary: colors.white,
    },
    primary: {
      main: colors.orange,
      light: colors.orange,
      dark: colors.orange,
    },
    secondary: {
      main: colors.teal,
      light: colors.teal,
      dark: colors.teal,
    },
    error: {
      main: colors.red,
      light: colors.red,
      dark: colors.red,
    },
  },
  typography: {
    useNextVariants: true, // Preparing for typography migration https://material-ui.com/style/typography/#migration-to-typography-v2
    fontSize: 14,
    htmlFontSize: 14,
    fontFamily: ['"Roboto"', 'sans-serif'].join(','),
    h3: {
      fontSize: '3rem',
      lineHeight: '1.5em',
      letterSpacing: '.25rem',
    },
    h4: {
      fontSize: '1.857rem',
      lineHeight: '1.5em',
      letterSpacing: 0,
      fontFamily: ['"Libre Franklin"', 'sans-serif'].join(','),
    },
    h5: {
      fontSize: '1.571rem',
      lineHeight: '1.5em',
      letterSpacing: '.15px',
      fontFamily: ['"Libre Franklin"', 'sans-serif'].join(','),
    },
    h6: {
      fontSize: '1.286rem',
      lineHeight: '1.5em',
      letterSpacing: '.15px',
    },
    altTitle: {
      fontFamily: ['"Libre Franklin"', 'sans-serif'].join(','),
      fontWeight: 500,
    },
    subtitle1: {
      fontSize: '1.071rem',
      lineHeight: '1.5em',
      letterSpacing: '.1px',
    },
    altSubheading: {
      fontFamily: ['"Libre Franklin"', 'sans-serif'].join(','),
      fontWeight: 700,
    },
    caption: {
      fontSize: '.857rem',
      lineHeight: '1.5em',
      letterSpacing: '.1px',
    },
    body2: {
      lineHeight: '1.5em',
      letterSpacing: '.1px',
    },
  },
  overrides: {
    MuiCardHeader: {
      title: {
        fontSize: '1.286rem',
        fontFamily: ['"Roboto"', 'sans-serif'].join(','),
      },
      subheader: {
        opacity: 1,
      },
    },
    MuiInput: {
      underline: {
        '&:before': {
          // underline color when textfield is inactive
          borderBottom: `2px solid ${colors.orange}`,
        },
        '&:hover:not($disabled):before': {
          // underline color when hovered
          borderBottom: `3px solid ${colors.orange}`,
        },
        '&:after': {
          // underline color when textfield is inactive
          borderBottom: `2px solid ${colors.orange}`,
        },
        '&$error:after': {
          // underline color when textfield is has been touched but no input
          borderBottom: `3px solid ${colors.red}`,
        },
      },
    },
    MuiButton: {
      root: {
        '&[hover-override~=green]': {
          backgroundColor: colors.green,
          color: colors.white,
          '&:hover:not($disabled)': {
            backgroundColor: colors.green,
          },
        },
        '&[hover-override~=darkPurple]': {
          backgroundColor: colors.darkPurple,
          color: colors.white,
          '&:hover:not($disabled)': {
            backgroundColor: colors.darkPurple,
          },
        },
        '&[hover-override~=magenta]': {
          backgroundColor: colors.magenta,
          color: colors.white,
          '&:hover:not($disabled)': {
            backgroundColor: colors.magenta,
          },
        },
        '&[hover-override~=black]': {
          backgroundColor: colors.black,
          color: colors.white,
          '&:hover:not($disabled)': {
            backgroundColor: colors.black,
          },
        },
        '&[hover-override~=kibana]': {
          backgroundColor: colors.peach,
          color: colors.black,
          '&:hover:not($disabled)': {
            backgroundColor: colors.peach,
          },
        },
        '&[hover-override~=cerebro]': {
          backgroundColor: '#7cbbbe',
          color: colors.black,
          '&:hover:not($disabled)': {
            backgroundColor: '#7cbbbe',
          },
        },
        '&[hover-override~=cockroachdb_admin]': {
          backgroundColor: '#7cbbbe',
          color: colors.black,
          '&:hover:not($disabled)': {
            backgroundColor: '#7cbbbe',
          },
        },
      },
    },
    MuiFab: {
      root: {
        '&[hover-override~=red]': {
          backgroundColor: colors.red,
          color: colors.white,
          '&:hover:not($disabled)': {
            backgroundColor: colors.red,
          },
        },
      },
    },
  },
  customColors: colors,
};

const darkThemeStyles = {
  palette: {
    ...globalThemeStyles.palette,
    type: 'dark',
    text: {
      primary: colors.white,
    },
    background: {
      default: colors.black,
      paper: colors.black,
    },
    typography: {
      useNextVariants: true, // Preparing for typography migration https://material-ui.com/style/typography/#migration-to-typography-v2
      suppressDeprecationWarnings: true,
      color: colors.coolGrey,
    },
  },
  overrides: {
    ...globalThemeStyles.overrides,
    MuiButton: {
      contained: {
        fontWeight: 'bold',
      },
      text: {
        color: colors.coolGrey,
        borderRadius: 0,
      },
      sizeLarge: {
        fontSize: '1.5rem',
        width: 340,
        maxWidth: '100%',
      },
    },
    MuiTypography: {
      colorPrimary: {
        color: colors.white,
      },
    },
    MuiFormLabel: {
      root: {
        // Need to specify focused class like this to get proper specificity for font color
        '&$focused': {
          color: colors.white,
        },
      },
    },
  },
};

const lightThemeStyles = {
  palette: {
    ...globalThemeStyles.palette,
    type: 'light',
    text: {
      primary: colors.black,
      secondary: colors.mediumGrey,
    },
    background: {
      default: colors.coolBlue,
      paper: colors.white,
    },
  },
  overrides: {
    ...globalThemeStyles.overrides,
    MuiFormLabel: {
      root: {
        // Need to specify focused class like this to get proper specificity for font color
        '&$focused': {
          color: colors.black,
        },
      },
    },
    MuiCardHeader: {
      ...globalThemeStyles.overrides.MuiCardHeader,
      root: {
        padding: 8,
        paddingTop: 8, // Specificity requires these to be explicitly declared.
        paddingBottom: 8,
        borderBottom: `1px solid ${colors.lightGrey}`,
      },
      avatar: {
        marginRight: 8,
      },
    },
    MuiCardContent: {
      ...globalThemeStyles.overrides.MuiCardContent,
      root: {
        padding: 8,
      },
    },
  },
};

// Constructing theme allows us to use theme.breakpoints for media overrides.
const customLightTheme = () => {
  let theme = createMuiTheme({ ...globalThemeStyles, ...lightThemeStyles });
  theme = {
    ...theme,
    overrides: {
      ...theme.overrides,
      MuiCardHeader: {
        ...theme.overrides.MuiCardHeader,
        root: {
          ...theme.overrides.MuiCardHeader.root,
          [theme.breakpoints.up('sm')]: {
            paddingLeft: 8,
            paddingRight: 8,
          },
        },
        action: {
          ...theme.overrides.MuiCardHeader.action,
          [theme.breakpoints.up('sm')]: {
            marginRight: -4,
          },
        },
      },
      MuiCardContent: {
        ...theme.overrides.MuiCardContent,
        root: {
          ...theme.overrides.MuiCardHeader.content,
          ...theme.mixins.gutters({
            paddingTop: 8,
            paddingBottom: 8,
            '&:last-child': {
              paddingBottom: 8,
            },
            [theme.breakpoints.up('sm')]: {
              paddingLeft: 8,
              paddingRight: 8,
            },
          }),
        },
      },
    },
  };
  return theme;
};

export const darkTheme = createMuiTheme({ ...globalThemeStyles, ...darkThemeStyles });
export const lightTheme = customLightTheme();
